import { getClassColor } from '../utils/colorUtils';
import React, { useState, useRef, useEffect } from 'react';
import {
  Image,
  Save,
  SkipForward,
  SkipBack,
  Trash2,
  Mouse,
  Square,
  Hand,
  Plus,
  X,
  BarChart3,
  Keyboard,
  Pencil
} from 'lucide-react';
import { ImageInfo, ClassInfo, Tool, AnnotationStats } from '../types';

interface SidebarProps {
  images: ImageInfo[];
  currentImageIndex: number;
  classes: ClassInfo[];
  currentClass: number;
  currentTool: Tool;
  stats: AnnotationStats | null;
  onImageSelect: (index: number) => void;
  onClassSelect: (classId: number) => void;
  onToolSelect: (tool: Tool) => void;
  onSave: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onClearAll: () => void;
  onClassEdit?: (classId: number, newName: string) => void;
  onClassAdd?: () => void;
  onClassDelete?: (classId: number) => void;
  isSaving: boolean;
}

const TOOL_INFO = {
  [Tool.POINTER]: { icon: Mouse, label: 'Select', shortcut: 'V' },
  [Tool.BBOX]: { icon: Square, label: 'Bounding Box', shortcut: 'N' },
  [Tool.PAN]: { icon: Hand, label: 'Pan', shortcut: 'H' }
};

export const Sidebar: React.FC<SidebarProps> = ({
  images,
  currentImageIndex,
  classes,
  currentClass,
  currentTool,
  stats,
  onImageSelect,
  onClassSelect,
  onToolSelect,
  onSave,
  onPrevious,
  onNext,
  onClearAll,
  onClassEdit,
  onClassAdd,
  onClassDelete,
  isSaving
}) => {
  const [editingClassId, setEditingClassId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingClassId !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingClassId]);

  const startEdit = (cls: ClassInfo) => {
    setEditingClassId(cls.id);
    setEditingValue(cls.name);
  };

  const saveEdit = () => {
    if (editingClassId !== null && onClassEdit && editingValue.trim()) {
      onClassEdit(editingClassId, editingValue.trim());
      setEditingClassId(null);
      setEditingValue('');
    }
  };

  const cancelEdit = () => {
    setEditingClassId(null);
    setEditingValue('');
  };

  const currentImage = images[currentImageIndex];

  return (
    <div className="sidebar">
      {/* Info Section */}
      <div className="sidebar-section">
        <h3 className="sidebar-section-header text-center">Dataset Info</h3>

        <div className="space-y-3">
          <div className="info-box">
            <div className="flex items-center gap-2 mb-3">
              <Image className="w-5 h-5 text-gray-500" />
              <span className="text-base text-gray-600">Current Image</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-base font-medium">{currentImage?.filename || 'None'}</p>
              <p className="text-base text-gray-500">
                {currentImageIndex + 1} of {images.length}
              </p>
            </div>
          </div>

          {stats && (
            <div className="info-box">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-5 h-5 text-gray-500" />
                <span className="text-base text-gray-600">Statistics</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-2 text-base">
                  <div>
                    <p className="text-gray-500">Total</p>
                    <p className="font-semibold">{stats.total_images}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Labeled</p>
                    <p className="font-semibold">{stats.labeled_images}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Annotations</p>
                    <p className="font-semibold">{stats.total_annotations}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500 mb-2">Progress</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.round((stats.labeled_images / stats.total_images) * 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-base">
                        {Math.round((stats.labeled_images / stats.total_images) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="sidebar-section">
        <h3 className="sidebar-section-header text-center">Navigation</h3>

        <div className="flex gap-2">
          <button
            onClick={onPrevious}
            disabled={currentImageIndex === 0}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100
                     hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <SkipBack className="w-5 h-5" />
            <span className="text-base">Previous</span>
          </button>

          <button
            onClick={onNext}
            disabled={currentImageIndex === images.length - 1}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100
                     hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <span className="text-base">Next</span>
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        <div className="info-box mt-3">
          <div className="flex items-center gap-2 mb-3">
            <Keyboard className="w-5 h-5 text-gray-500" />
            <span className="text-base text-gray-600">Shortcuts</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="space-y-1 text-base text-gray-600">
              <div className="flex justify-between">
                <span>Previous/Next:</span>
                <span className="font-mono">← →</span>
              </div>
              <div className="flex justify-between">
                <span>Save:</span>
                <span className="font-mono">Cmd+S</span>
              </div>
              <div className="flex justify-between">
                <span>Delete:</span>
                <span className="font-mono">Del</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tools */}
      <div className="sidebar-section">
        <h3 className="sidebar-section-header text-center">Tools</h3>

        <div className="space-y-2">
          {Object.values(Tool).map((tool) => {
            const info = TOOL_INFO[tool];
            const Icon = info.icon;

            return (
              <button
                key={tool}
                onClick={() => onToolSelect(tool)}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                  currentTool === tool
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-base ml-3">{info.label}</span>
                <kbd className="ml-auto px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-200 rounded min-w-[28px] text-center">
                  {info.shortcut}
                </kbd>
              </button>
            );
          })}
        </div>
      </div>

      {/* Classes */}
      <div className="sidebar-section classes-section">
        <div className="classes-header">
          <h3 className="sidebar-section-header">Classes</h3>
          {onClassAdd && (
            <button
              onClick={onClassAdd}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
              title="Add new class"
            >
              <Plus className="w-4 h-4" />
              Add Class
            </button>
          )}
        </div>

        <div className="classes-list custom-scrollbar">
          {classes.map((cls, index) => (
            <div
              key={cls.id}
              className={`class-item ${currentClass === cls.id ? 'selected' : ''} ${editingClassId === cls.id ? 'editing' : ''}`}
              onClick={() => onClassSelect(cls.id)}
            >
              <div className="color-indicator" style={{ backgroundColor: getClassColor(cls.id) }}></div>

              {editingClassId === cls.id ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onBlur={saveEdit}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveEdit();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEdit();
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="class-name">{cls.name}</span>
              )}

              {onClassDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete class "${cls.name}"? This will affect all annotations!`)) {
                      onClassDelete(cls.id);
                    }
                  }}
                  className="delete-btn hover:bg-red-200 text-red-600"
                  title="Delete class"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {editingClassId !== cls.id && onClassEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startEdit(cls);
                  }}
                  className="edit-btn hover:bg-gray-200"
                  title="Edit class name"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}

              <kbd className="px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-200 rounded min-w-[28px] text-center">
                {index}
              </kbd>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="sidebar-section">
        <h3 className="sidebar-section-header text-center">Actions</h3>

        <div className="space-y-2">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="w-full flex items-center justify-between px-4 py-3 bg-blue-600 hover:bg-blue-700
                     disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <Save className="w-5 h-5" />
              <span className="text-base">{isSaving ? 'Saving...' : 'Save Annotations'}</span>
            </div>
            <span className="text-white opacity-90 text-sm">⌘S</span>
          </button>

          <button
            onClick={onClearAll}
            className="w-full flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700
                     text-white rounded-lg transition-colors"
          >
            <Trash2 className="w-5 h-5" />
            <span className="text-base">Clear All</span>
          </button>
        </div>
      </div>

    </div>
  );
};